import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';
import { ProfileTab, TeamsTab, SettingsTab } from '../components/AccountPanel.jsx';
import { NotificationSettings } from './NotificationsPage.jsx';

const noop = () => {};

/** Shared frame for the account-menu sections opened as full pages. */
function AccountPage({ title, sub, children }) {
  const auth = useAuth();
  useEffect(() => {
    document.title = `${title} · HeatWatch`;
  }, [title]);
  if (!auth || auth.loading) return <div className="app"><div className="loading">Loading…</div></div>;
  if (!auth.available) return <div className="app"><div className="banner warn">Accounts aren’t available on this server.</div></div>;
  if (!auth.user) return <Navigate to="/login" replace />;
  return (
    <div className="app account-page">
      <div className="page-head">
        <Link to="/" className="btn-link">
          ← Map
        </Link>
        <h1>{title}</h1>
        {sub && <p className="muted">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

export function ProfilePage() {
  return (
    <AccountPage title="Profile" sub="Your name, password and account.">
      <div className="form-card page-card">
        <ProfileTab onDone={noop} />
      </div>
    </AccountPage>
  );
}

export function TeamsPage() {
  const auth = useAuth();
  const teams = auth?.teams ?? [];
  return (
    <AccountPage title="Teams" sub="Sites, routes and alerts are shared with everyone on a team. Owners and admins manage members and invite codes.">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="left">Team</th>
              <th className="left">Your role</th>
              <th>Members</th>
              <th>Sites</th>
              <th className="left" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => (
              <tr key={t.id}>
                <td className="left">
                  <Link to={`/teams/${t.id}`}>{t.name}</Link>
                </td>
                <td className="left">{t.role}</td>
                <td>{t.memberCount ?? '—'}</td>
                <td>{t.siteCount ?? '—'}</td>
                <td className="left">
                  <Link className="btn" to={`/teams/${t.id}`}>
                    Members &amp; permissions
                  </Link>{' '}
                  <Link className="btn" to={`/sites?team=${t.id}`}>
                    Shared sites
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form-card page-card">
        <h2>Join or create a team</h2>
        <TeamsTab onDone={noop} />
      </div>
    </AccountPage>
  );
}

const DIR = { above: '≥', below: '≤' };

/** The app's weather alert thresholds and their sources (read-only). */
function ThresholdTable() {
  const [t, setT] = useState(null);
  useEffect(() => {
    api('/alerts/thresholds')
      .then(setT)
      .catch(() => setT(false));
  }, []);
  if (t === false) return <div className="banner err">Couldn’t load the alert thresholds.</div>;
  if (!t) return <div className="loading">Loading thresholds…</div>;
  return (
    <div className="table-wrap">
      <table className="threshold-table">
        <thead>
          <tr>
            <th className="left">Alert</th>
            <th>Advisory</th>
            <th>Warning</th>
            <th>Critical</th>
            <th className="left">Source</th>
          </tr>
        </thead>
        <tbody>
          {t.thresholds.map((x) => (
            <tr key={x.key}>
              <td className="left">
                {x.title}
                {x.estimated && <span className="muted"> (estimated)</span>}
              </td>
              {x.levels.map((v, i) => (
                <td key={i}>
                  {DIR[x.dir]} {v} {x.unit}
                </td>
              ))}
              <td className="left small-note">{x.source}</td>
            </tr>
          ))}
          {t.weatherCodes.map((x) => (
            <tr key={x.key}>
              <td className="left">{x.title}</td>
              <td colSpan={3}>Modeled weather codes in the next 24 h</td>
              <td className="left small-note">{x.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SettingsPage() {
  return (
    <AccountPage title="Settings" sub="Map, units, alert thresholds and notifications.">
      <div className="form-card page-card">
        <h2>Map &amp; display</h2>
        <SettingsTab />
      </div>
      <section className="form-card page-card">
        <h2>Weather alert thresholds</h2>
        <p className="muted">
          HeatWatch raises app threshold alerts for your sites and routes when modeled conditions over the next 24 hours reach
          these levels. They are this app’s calculations, not official warnings or legal limits. Alerts are recorded from the
          advisory level; choose what notifies you below.
        </p>
        <ThresholdTable />
      </section>
      <NotificationSettings />
    </AccountPage>
  );
}
